<template>
  <section class="section is-medium pricing-section">
    <div class="container">
      <div class="has-text-centered mb-6">
        <h2 class="title is-2 has-line animate fade-in-up">
          {{ $t('landing.pricing-title') }}
        </h2>
        <p class="subtitle is-5 animate fade-in-up animate--delay-1">
          Choose the perfect plan for your music career
        </p>
      </div>

      <!-- Pricing Highlight Banner -->
      <div class="pricing-highlight-banner animate fade-in-up animate--delay-1">
        <div class="banner-content">
          <div class="banner-icon">
            <i class="fas fa-star" />
          </div>
          <div class="banner-text">
            <h3 class="title is-4">
              Best Value in Music Distribution
            </h3>
            <p>Start free, scale as you grow. No hidden fees, no surprises.</p>
          </div>
        </div>
      </div>

      <!-- Billing Toggle -->
      <div class="billing-toggle animate fade-in-up animate--delay-1 has-text-centered">
        <div class="toggle-container">
          <span
            class="toggle-label"
            :class="{ 'is-active': !isYearly }"
          >
            {{ $t('settings.monthly') }}
          </span>
          <div
            class="toggle-switch"
            @click="toggleBilling"
          >
            <div
              class="toggle-slider"
              :class="{ 'is-yearly': isYearly }"
            />
          </div>
          <span
            class="toggle-label"
            :class="{ 'is-active': isYearly }"
          >
            {{ $t('settings.yearly') }}
            <span class="savings-badge">{{ $t('settings.save-money') }}</span>
          </span>
        </div>
      </div>

      <div class="columns is-multiline is-centered">
        <!-- Free Tier -->
        <div class="column is-4">
          <div class="pricing-card animate fade-in-up">
            <div class="pricing-header">
              <h3 class="pricing-tier">
                Free
              </h3>
              <div class="pricing-price">
                <span class="price">$0</span>
                <span class="billing">/{{ $t('general.year') }}</span>
              </div>
              <p class="pricing-description">
                {{ $t('settings.plan-descriptions.free') }}
              </p>
            </div>
            <div class="pricing-features">
              <p class="feature-label">
                {{ $t('landing.pricing-includes') }}
              </p>
              <ul class="feature-list">
                <li><i class="fas fa-check" /> 85% {{ $t('royalties.royalty-split') }}</li>
                <li><i class="fas fa-check" /> 1 {{ $t('navigation.artists') }}</li>
                <li><i class="fas fa-check" /> 1 {{ $t('releases.release') }} {{ $t('general.per') }} {{ $t('general.month') }}</li>
                <li><i class="fas fa-check" /> {{ $t('royalties.min-payment') }}: $100</li>
                <li class="disabled">
                  <i class="fas fa-times" /> {{ $t('settings.custom-label') }}
                </li>
                <li class="disabled">
                  <i class="fas fa-times" /> {{ $t('settings.social-verification') }}
                </li>
              </ul>
            </div>
            <div class="pricing-cta">
              <router-link
                to="/auth/signup"
                class="button is-primary is-fullwidth"
              >
                {{ $t('landing.cta-primary') }}
              </router-link>
            </div>
          </div>
        </div>

        <!-- PRO Tier -->
        <div class="column is-4">
          <div class="pricing-card is-featured animate fade-in-up animate--delay-1">
            <div class="popular-badge">
              {{ $t('general.popular') }}
            </div>
            <div class="pricing-header">
              <h3 class="pricing-tier">
                PRO
              </h3>
              <div class="pricing-price">
                <span class="price">${{ isYearly ? '49' : '9.99' }}</span>
                <span class="billing">/{{ isYearly ? $t('general.year') : $t('general.month') }}</span>
                <div
                  v-if="!isYearly"
                  class="monthly-note"
                >
                  <small>{{ $t('settings.billed-monthly') }}</small>
                </div>
                <div
                  v-if="isYearly"
                  class="yearly-savings"
                >
                  <small class="savings-text">{{ $t('settings.save-yearly', { amount: '$70.88' }) }}</small>
                </div>
              </div>
              <p class="pricing-description">
                {{ $t('settings.plan-descriptions.pro') }}
              </p>
            </div>
            <div class="pricing-features">
              <p class="feature-label">
                {{ $t('landing.pricing-includes') }}
              </p>
              <ul class="feature-list">
                <li><i class="fas fa-check" /> 100% {{ $t('royalties.royalty-split') }}*</li>
                <li><i class="fas fa-check" /> 5 {{ $t('navigation.artists') }}</li>
                <li><i class="fas fa-check" /> 25 {{ $t('navigation.releases') }} {{ $t('general.per') }} {{ $t('general.month') }}</li>
                <li><i class="fas fa-check" /> {{ $t('settings.custom-label') }}</li>
                <li><i class="fas fa-check" /> {{ $t('settings.social-verification') }}</li>
                <li><i class="fas fa-check" /> {{ $t('settings.premium-stores') }}</li>
                <li><i class="fas fa-check" /> {{ $t('settings.content-id') }}</li>
                <li><i class="fas fa-check" /> {{ $t('royalties.min-payment') }}: $50</li>
                <template v-if="isProFeaturesExpanded">
                  <li><i class="fas fa-check" /> {{ $t('settings.ai-tools') }}</li>
                  <li><i class="fas fa-check" /> {{ $t('settings.social-platforms') }}</li>
                  <li><i class="fas fa-check" /> {{ $t('settings.faster-payments') }}</li>
                  <li><i class="fas fa-check" /> {{ $t('settings.faster-releases') }}</li>
                  <li><i class="fas fa-check" /> {{ $t('settings.dedicated-support') }}</li>
                  <li><i class="fas fa-check" /> {{ $t('settings.all-features') }}</li>
                  <li><i class="fas fa-check" /> {{ $t('settings.presave') }}</li>
                  <li><i class="fas fa-check" /> {{ $t('settings.playlist-submission') }}</li>
                </template>
                <li class="expand-features">
                  <button
                    class="expand-button"
                    @click="isProFeaturesExpanded = !isProFeaturesExpanded"
                  >
                    <span>{{ isProFeaturesExpanded ? $t('general.show-less') : $t('general.show-more') }}</span>
                    <i
                      class="fas"
                      :class="isProFeaturesExpanded ? 'fa-chevron-up' : 'fa-chevron-down'"
                    />
                  </button>
                </li>
              </ul>
            </div>
            <div class="pricing-cta">
              <button
                class="button is-primary is-fullwidth"
                @click="upgradePlan('pro')"
              >
                {{ $t('settings.change-plan') }}
              </button>
              <div
                v-if="!isYearly"
                class="upgrade-suggestion mt-2"
              >
                <small class="has-text-primary">
                  <i class="fas fa-info-circle mr-1" />
                  {{ $t('settings.yearly-suggestion') }}
                </small>
              </div>
            </div>
          </div>
        </div>

        <!-- Advanced Tier -->
        <div class="column is-4">
          <div class="pricing-card animate fade-in-up animate--delay-2">
            <div class="pricing-header">
              <h3 class="pricing-tier">
                Advanced
              </h3>
              <div class="pricing-price">
                <span class="price">$199</span>
                <span class="billing">/{{ $t('general.year') }}</span>
                <div
                  v-if="!isYearly"
                  class="yearly-only-note"
                >
                  <small class="yearly-only-text">{{ $t('settings.yearly-only') }}</small>
                </div>
                <div
                  v-if="isYearly"
                  class="yearly-savings"
                >
                  <small class="savings-text">{{ $t('settings.best-value') }}</small>
                </div>
              </div>
              <p class="pricing-description">
                {{ $t('settings.plan-descriptions.advanced') }}
              </p>
            </div>
            <div class="pricing-features">
              <p class="feature-label">
                {{ $t('landing.pricing-includes') }}
              </p>
              <ul class="feature-list">
                <li><i class="fas fa-check" /> {{ $t('settings.pro-features') }}</li>
                <li><i class="fas fa-check" /> 100 {{ $t('navigation.releases') }} {{ $t('general.per') }} {{ $t('general.month') }}</li>
                <li><i class="fas fa-check" /> 50 {{ $t('navigation.artists') }}</li>
              </ul>
            </div>
            <div class="pricing-cta">
              <button
                class="button is-primary is-outlined is-fullwidth"
                :disabled="!isYearly"
                @click="upgradePlan('advanced')"
              >
                {{ $t('settings.change-plan') }}
              </button>
              <div
                v-if="!isYearly"
                class="yearly-only-cta mt-2"
              >
                <small class="has-text-warning">
                  <i class="fas fa-calendar-alt mr-1" />
                  {{ $t('settings.switch-yearly-advanced') }}
                </small>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script setup>
import { RouterLink } from 'vue-router';
import { ref, computed, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRoute } from 'vue-router';
import { useHead } from '@vueuse/head';
import { useAuthStore } from '@/store/auth';
import userService from '@/services/userService';
import { SITE_URL, SITE_NAME } from '@/config/seo';

const stripe = ref(null);
const isLoading = ref(false);
const isYearly = ref(true);
const route = useRoute();
const authStore = useAuthStore();

const isProFeaturesExpanded = ref(false);
const { t } = useI18n();

const offersJsonLd = computed(() => ({
  '@context': 'https://schema.org',
  '@type': 'Product',
  name: `${SITE_NAME} Music Distribution`,
  description: t('landing.page_description'),
  brand: {
    '@type': 'Brand',
    name: SITE_NAME,
  },
  offers: [
    {
      '@type': 'Offer',
      name: 'Free',
      description: t('settings.plan-descriptions.free'),
      price: '0',
      priceCurrency: 'USD',
      url: `${SITE_URL}/auth/signup`,
      availability: 'https://schema.org/InStock',
      category: 'Free',
    },
    {
      '@type': 'Offer',
      name: 'PRO',
      description: t('settings.plan-descriptions.pro'),
      price: isYearly.value ? '49' : '9.99',
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: isYearly.value ? '49' : '9.99',
        priceCurrency: 'USD',
        billingDuration: isYearly.value ? 'P1Y' : 'P1M',
        unitText: isYearly.value ? 'YEAR' : 'MONTH',
      },
      url: `${SITE_URL}/upgrade`,
      availability: 'https://schema.org/InStock',
      category: 'Subscription',
    },
    {
      '@type': 'Offer',
      name: 'Advanced',
      description: t('settings.plan-descriptions.advanced'),
      price: '199',
      priceCurrency: 'USD',
      priceSpecification: {
        '@type': 'UnitPriceSpecification',
        price: '199',
        priceCurrency: 'USD',
        billingDuration: 'P1Y',
        unitText: 'YEAR',
      },
      url: `${SITE_URL}/upgrade`,
      availability: 'https://schema.org/InStock',
      category: 'Subscription',
    },
  ],
}));

useHead({
  script: [
    {
      type: 'application/ld+json',
      key: 'pricing-product-jsonld',
      children: computed(() => JSON.stringify(offersJsonLd.value)),
    },
  ],
});

const toggleBilling = () => {
  isYearly.value = !isYearly.value;
};

const initializeStripe = () => {
  let stripeKey = 'pk_live_Fy0LR9SOyRoSrq4Byoj3mQtS00GjNUCMRm';

  if (window.location.href.match(/localhost|cms-development/)) {
    stripeKey = 'pk_test_WD9FBh5jNgV5uO1eFQBkLdAl000Ey6vyno';
  }

  stripe.value = window.Stripe(stripeKey);
};

const upgradePlan = async (plan) => {
  if (!stripe.value) {
    alert(t('settings.stripe-not-loaded'));
    return;
  }

  isLoading.value = true;

  try {
    let priceId;
    if (plan === 'pro') {
      priceId = isYearly.value ? 'price_1LFOeXK9qKRAJ5zqfGt7fcx8' : 'price_1HccB9K9qKRAJ5zqKJCo4gf0';
    } else {
      priceId = 'plan_GxucdZQjJLKPiZ'; // Advanced is always yearly
    }

    const subscriptionData = {
      from: route.query.from || 'landing',
    };

    if (window.localStorage) {
      subscriptionData.utm_campaign = localStorage.getItem('utm_campaign') || route.query.utm_campaign || '';
      subscriptionData.utm_source = localStorage.getItem('utm_source') || route.query.utm_source || '';
      subscriptionData.utm_medium = localStorage.getItem('utm_medium') || route.query.utm_medium || '';
    }

    window.gtag && window.gtag('event', 'conversion', { 'send_to': 'AW-323371496/1fPACNGmjZ0YEOiDmZoB' });

    userService.setSubscriptionTrackingData(subscriptionData).finally(async () => {
      const result = await stripe.value.redirectToCheckout({
        lineItems: [{ price: priceId, quantity: 1 }],
        mode: 'subscription',
        customerEmail: authStore.currentUser?.email,
        successUrl: 'https://indiefy.net/v1/api/user/upgrading?session_id={CHECKOUT_SESSION_ID}&beta=1',
        cancelUrl: 'https://indiefy.net/upgrade?cancelled=1',
      });

      if (result.error) {
        alert(result.error.message);
      }
    });
  } catch (error) {
    console.error('Stripe checkout error:', error);
    alert(t('settings.upgrade-error'));
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  initializeStripe();
});
</script>

<style scoped lang="scss">
@import "@/styles/abstracts/colors";
@import "@/styles/abstracts/typography";
@import "@/styles/abstracts/animations";

.billing-toggle {
  margin: 0 0 2.5rem;
  display: flex;
  justify-content: center;

  .toggle-container {
    display: flex;
    align-items: center;
    gap: 1rem;
    background-color: var(--color-card-background, white);
    padding: 0.75rem 1.5rem;
    border-radius: 50px;
    box-shadow: 0 4px 16px rgba(0, 0, 0, 0.08);

    .body--dark & {
      background-color: var(--color-card-background, #1e1e1e);
      box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
    }
  }

  .toggle-label {
    font-weight: 600;
    color: var(--color-text-light, #666);
    transition: color 0.3s ease;
    position: relative;

    &.is-active {
      color: var(--color-text, #333);
    }

    .savings-badge {
      display: inline-block;
      background: linear-gradient(135deg, $indiefy-blue, $indiefy-pink);
      color: white;
      font-size: 0.7rem;
      font-weight: 700;
      padding: 0.2rem 0.5rem;
      border-radius: 12px;
      margin-left: 0.5rem;
      text-transform: uppercase;
      letter-spacing: 0.5px;
    }
  }

  .toggle-switch {
    width: 60px;
    height: 30px;
    background-color: #e0e0e0;
    border-radius: 15px;
    position: relative;
    cursor: pointer;
    transition: background-color 0.3s ease;

    .body--dark & {
      background-color: #4a4a4a;
    }

    .toggle-slider {
      width: 26px;
      height: 26px;
      background: linear-gradient(135deg, $indiefy-blue, $indiefy-pink);
      border-radius: 50%;
      position: absolute;
      top: 2px;
      left: 2px;
      transition: transform 0.3s ease;
      box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);

      &.is-yearly {
        transform: translateX(30px);
      }
    }
  }
}

.monthly-note,
.yearly-savings,
.yearly-only-note {
  margin-top: 0.5rem;
  text-align: center;

  small {
    font-size: 0.85rem;
    font-weight: 500;
  }
}

.yearly-savings .savings-text {
  color: $success;
  font-weight: 600;
}

.yearly-only-note .yearly-only-text {
  color: var(--color-text-light, #999);
}

.upgrade-suggestion,
.yearly-only-cta {
  text-align: center;
  padding: 0.5rem;
  border-radius: 8px;
  background-color: rgba($indiefy-blue, 0.05);

  .body--dark & {
    background-color: rgba($indiefy-blue, 0.15);
  }

  small {
    font-weight: 500;
  }
}

.yearly-only-cta {
  background-color: rgba($warning, 0.1);

  .body--dark & {
    background-color: rgba($warning, 0.2);
  }
}

.button {
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
}

.pricing-section {
  background: linear-gradient(135deg,
    rgba(lighten($indiefy-blue, 38%), 0.5) 0%,
    rgba(lighten($indiefy-lavender, 28%), 0.5) 100%
  );

  .body--dark & {
    background: linear-gradient(135deg,
      rgba(darken($indiefy-blue, 15%), 0.3) 0%,
      rgba(darken($indiefy-lavender, 5%), 0.3) 100%
    );
  }
}

.title.has-line {
  position: relative;
  padding-bottom: 1.5rem;
  color: var(--color-text, #000);

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 80px;
    height: 4px;
    background: linear-gradient(90deg, $indiefy-blue, $indiefy-pink);
    border-radius: 2px;
  }
}

.pricing-card {
  position: relative;
  background-color: var(--color-background, white);
  border-radius: 16px;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.08);
  overflow: hidden;
  height: 100%;
  display: flex;
  flex-direction: column;
  transition: transform 0.3s ease, box-shadow 0.3s ease;

  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);
  }

  &.is-featured {
    transform: scale(1.05);
    z-index: 2;
    border: 2px solid $indiefy-pink;
    box-shadow: 0 20px 50px rgba(0, 0, 0, 0.15);

    &:hover {
      transform: scale(1.05) translateY(-10px);
    }

    .pricing-tier {
      color: $indiefy-pink;
    }
  }
}

.popular-badge {
  position: absolute;
  top: 0;
  right: 0;
  background-color: $indiefy-pink;
  color: white;
  font-size: 0.8rem;
  font-weight: bold;
  padding: 0.3rem 1rem;
  border-bottom-left-radius: 8px;
}

.pricing-header {
  padding: 2rem 2rem 1.5rem;
  text-align: center;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);

  .body--dark & {
    border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  }
}

.pricing-tier {
  font-size: 1.5rem;
  font-weight: 700;
  margin-bottom: 1rem;
  color: var(--color-text, #333);
}

.pricing-price {
  margin-bottom: 1rem;

  .price {
    font-family: $family-display;
    font-size: 2.5rem;
    font-weight: 700;
    color: var(--color-text, #333);
  }

  .billing {
    font-size: 0.9rem;
    color: var(--color-text-light, #666);
    margin-left: 0.3rem;
  }
}

.pricing-description {
  font-size: 0.95rem;
  color: var(--color-text-light, #666);
}

.pricing-features {
  padding: 1.5rem 2rem;
  flex-grow: 1;

  .feature-label {
    font-weight: 600;
    font-size: 1rem;
    margin-bottom: 1rem;
    color: var(--color-text, #333);
  }

  .feature-list {
    list-style: none;
    padding: 0;
    margin: 0;

    li {
      padding: 0.5rem 0;
      font-size: 0.95rem;
      display: flex;
      align-items: center;
      color: var(--color-text, #333);

      i {
        width: 20px;
        margin-right: 10px;
        color: $indiefy-blue;
      }

      &.disabled {
        color: var(--color-text-light, #999);

        i {
          color: #ccc;

          .body--dark & {
            color: #666;
          }
        }
      }
    }
  }
}

.pricing-cta {
  padding: 1.5rem 2rem 2rem;
}

@media screen and (max-width: 768px) {
  .pricing-card {
    margin-bottom: 2rem;

    &.is-featured {
      transform: scale(1);

      &:hover {
        transform: translateY(-10px);
      }
    }
  }
}

.expand-features {
  margin-top: 1rem !important;
  justify-content: center !important;

  .expand-button {
    background: transparent;
    border: none;
    color: $indiefy-blue;
    font-weight: 600;
    font-size: 0.9rem;
    padding: 0.5rem 1rem;
    border-radius: 20px;
    cursor: pointer;
    transition: all 0.3s ease;
    display: flex;
    align-items: center;
    gap: 0.5rem;

    &:hover {
      background: rgba($indiefy-blue, 0.1);
      transform: translateY(-2px);
    }

    i {
      font-size: 0.8rem;
      transition: transform 0.3s ease;
    }
  }
}

.feature-list {
  li {
    animation: fadeIn 0.3s ease;
  }
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.pricing-highlight-banner {
  background: linear-gradient(135deg, $indiefy-blue, $indiefy-pink);
  border-radius: 16px;
  padding: 2rem;
  margin-bottom: 3rem;
  color: white;
  box-shadow: 0 8px 32px rgba($indiefy-blue, 0.3);

  .banner-content {
    display: flex;
    align-items: center;
    gap: 1.5rem;
    max-width: 600px;
    margin: 0 auto;
    text-align: center;

    .banner-icon {
      font-size: 2rem;
      color: rgba(255, 255, 255, 0.9);
    }

    .banner-text {
      h3 {
        color: white;
        margin-bottom: 0.5rem;
      }

      p {
        color: rgba(255, 255, 255, 0.9);
        margin: 0;
        font-size: 1.1rem;
      }
    }
  }

  @media (max-width: 768px) {
    padding: 1.5rem;

    .banner-content {
      flex-direction: column;
      gap: 1rem;

      .banner-icon {
        font-size: 1.5rem;
      }

      .banner-text p {
        font-size: 1rem;
      }
    }
  }
}
</style>